// `aliases` maps retired tab values to their replacement, so old links and bookmarks land on the
// tab that absorbed them instead of falling back to the default.
const createModuleTabs = (basePath, tabs, defaultTab, tabParam = 'tab', aliases = {}) => {
  const normalize = tab => aliases[tab] ?? tab;
  return {
    basePath,
    tabParam,
    tabs,
    defaultTab,
    path: (tab = defaultTab, extraParams = undefined) => {
      const params = new URLSearchParams(extraParams);
      params.set(tabParam, tabs.includes(normalize(tab)) ? normalize(tab) : defaultTab);
      return `${basePath}?${params.toString()}`;
    },
    resolve: (search = '') => {
      const tab = normalize(new URLSearchParams(search).get(tabParam));
      return tabs.includes(tab) ? tab : defaultTab;
    },
  };
};

export const REMEDIATION_HUB_TABS = createModuleTabs('/remediation-hub', ['policies', 'reports'], 'policies');
export const DATA_SECURITY_TABS = createModuleTabs('/data-security', ['overview', 'insights'], 'overview');
// Risk (config + runtime) and Runtime Findings (runtime only) merged into one Findings tab.
export const API_SECURITY_TABS = createModuleTabs(
  '/api-security',
  ['overview', 'inventory', 'findings', 'shadow-apis'],
  'overview',
  'tab',
  { risk: 'findings', 'runtime-findings': 'findings' },
);
export const APPLICATION_SECURITY_TABS = createModuleTabs(
  '/application-security',
  ['repository-scanning', 'registry-scanning', 'dast-scanning'],
  'repository-scanning',
);
// `tab` is already used by the Events page for Overview, Drift, Threat, and Remediation.
// Keep the module view in a separate query parameter so both selections can coexist.
export const THREAT_TABS = createModuleTabs('/threat', ['threat-story', 'events'], 'events', 'threatTab');

// Kubernetes runtime detection: the stories the correlator builds from runtime alerts, and the alerts themselves.
// The stories lead: a link that names no tab (the sidebar's Runtime Detection) lands on them, and the alerts table
// the page used to be is the second tab.
export const K8S_RUNTIME_TABS = createModuleTabs(
  '/kubernetes-live/runtime',
  ['threat-story', 'runtime-detection'],
  'threat-story',
  'runtimeTab',
);

// The nested `tab` values of the Events module tab, in the order the ActionTypeBar renders them.
export const THREAT_EVENTS_TABS = [
  'Overview',
  'Drift',
  'Remediation',
  'Vulnerability',
  'Threat',
  'Read',
  'ActionHistory',
];
export const THREAT_EVENTS_DEFAULT_TAB = 'Overview';
export const THREAT_EVENTS_ACTION_HISTORY_TAB = 'ActionHistory';

// Module-tab values minted before `/cdr` became `/threat`. Overview and All Events were separate
// module tabs then; all three are views of the single `events` tab now.
export const LEGACY_CDR_TAB_PARAM = 'cdrTab';
export const LEGACY_CDR_TABS = {
  overview: { tab: 'events', nestedTab: 'Overview' },
  'all-events': { tab: 'events', nestedTab: 'Drift' },
  'action-history': { tab: 'events', nestedTab: THREAT_EVENTS_ACTION_HISTORY_TAB },
};

// Threat Story used to be a nested tab of All Events, so links to it pin `?tab=ThreatStory`.
export const LEGACY_THREAT_STORY_NESTED_TAB = 'ThreatStory';

// Action History used to be a module tab of its own, so links to it pin `?threatTab=action-history`.
export const LEGACY_ACTION_HISTORY_MODULE_TAB = 'action-history';

/**
 * Resolve the active Threat module tab, honouring the deep links minted before Threat Story was
 * promoted from a nested tab to a module tab. `?tab=ThreatStory` wins over the module parameter,
 * because such links pair it with the module tab that used to host it; the Threat page rewrites the
 * URL once it has been resolved, so the marker never lingers to hijack a later tab change.
 *
 * @param {string} [search] a query string, with or without the leading `?`
 * @returns {string} one of THREAT_TABS.tabs
 */
export const resolveThreatTab = (search = '') => {
  const params = new URLSearchParams(search);
  if (params.get('tab') === LEGACY_THREAT_STORY_NESTED_TAB) return 'threat-story';

  const tab = params.get(THREAT_TABS.tabParam);
  if (THREAT_TABS.tabs.includes(tab)) return tab;
  // Action History is a nested tab of Events now, so its old module-tab links land on Events.
  if (tab === LEGACY_ACTION_HISTORY_MODULE_TAB) return 'events';

  return LEGACY_CDR_TABS[params.get(LEGACY_CDR_TAB_PARAM)]?.tab ?? THREAT_TABS.defaultTab;
};

/**
 * Resolve the nested Events tab (`?tab=`) for the Events module tab. `?threatTab=action-history` is
 * honoured as the nested Action History tab, so the deep links minted while it was a module tab of
 * its own still open it; the Threat page rewrites such a URL once it has been resolved.
 *
 * @param {string} [search] a query string, with or without the leading `?`
 * @returns {string} one of THREAT_EVENTS_TABS
 */
export const resolveThreatEventsTab = (search = '') => {
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  if (THREAT_EVENTS_TABS.includes(tab)) return tab;

  if (params.get(THREAT_TABS.tabParam) === LEGACY_ACTION_HISTORY_MODULE_TAB) {
    return THREAT_EVENTS_ACTION_HISTORY_TAB;
  }

  return THREAT_EVENTS_DEFAULT_TAB;
};
export const WORKLOAD_TABS = createModuleTabs('/workload', ['dashboard', 'vulnerabilities'], 'dashboard');
export const IDENTITY_HUMAN_TABS = createModuleTabs(
  '/identity',
  ['users', 'roles', 'policies', 'groups', 'idp', 'jit'],
  'users',
  'humanIdentityTab',
);
export const IDENTITY_TABS = createModuleTabs('/identity', ['overview', 'human-identity', 'non-human-identity'], 'overview');
export const IDENTITY_NHI_TABS = createModuleTabs('/identity', ['overview', 'findings'], 'overview', 'nhiTab');

/**
 * Deep link into a Human Identity sub-tab. The tab params lead the query string, so links read
 * `/identity?tab=human-identity&humanIdentityTab=users&activeUser=true` instead of going through the
 * legacy `/identity/users` redirect, which appends them after the deep-link params.
 *
 * @param {string} [humanIdentityTab] one of IDENTITY_HUMAN_TABS.tabs; anything else falls back to its default
 * @param {Record<string, string> | string} [extraParams] deep-link params, as an object or a query string
 * @returns {string}
 */
export const identityHumanTabPath = (humanIdentityTab = IDENTITY_HUMAN_TABS.defaultTab, extraParams = undefined) => {
  const params = new URLSearchParams({
    [IDENTITY_TABS.tabParam]: 'human-identity',
    [IDENTITY_HUMAN_TABS.tabParam]: IDENTITY_HUMAN_TABS.tabs.includes(humanIdentityTab)
      ? humanIdentityTab
      : IDENTITY_HUMAN_TABS.defaultTab,
  });
  for (const [key, value] of new URLSearchParams(extraParams)) {
    if (key === IDENTITY_TABS.tabParam || key === IDENTITY_HUMAN_TABS.tabParam) continue;
    params.append(key, value);
  }
  return `${IDENTITY_TABS.basePath}?${params.toString()}`;
};
export const DATABASE_SECURITY_TABS = createModuleTabs('/database-security', ['overview', 'exceptions'], 'overview');
export const ATTACK_SURFACE_TABS = createModuleTabs(
  '/attack-surface',
  ['dashboard', 'findings', 'playbooks'],
  'dashboard',
);
export const AI_SECURITY_TABS = createModuleTabs(
  '/ai-security',
  ['overview', 'inventory', 'runtime-threat', 'findings'],
  'overview',
);
// Kubernetes Live is too large for module tabs, so its Vulnerabilities page gets its own tab set
// (see PAGE_TABS in navConfig). It keeps the `activeTab` parameter it used when its tabs were
// numeric. Findings, CVEs, Workloads and Images used to be tabs of their own (and before that the
// numbers 0–2); they are sub tabs of Vulnerabilities now, so those old values land there.
export const K8S_VULNERABILITIES_TABS = createModuleTabs(
  '/kubernetes-live/vulnerabilities',
  ['dashboard', 'vulnerabilities', 'sbom', 'host'],
  'dashboard',
  'activeTab',
  {
    0: 'vulnerabilities',
    1: 'vulnerabilities',
    2: 'vulnerabilities',
    3: 'sbom',
    4: 'host',
    findings: 'vulnerabilities',
    cves: 'vulnerabilities',
    workloads: 'vulnerabilities',
    images: 'vulnerabilities',
  },
);

// The sub tabs of the Vulnerabilities tab, kept in their own parameter beside `activeTab`.
export const K8S_VULNERABILITIES_SUB_TABS = {
  tabParam: 'vulnerabilityTab',
  tabs: ['findings', 'cves', 'workloads', 'images'],
  defaultTab: 'findings',
};

// `activeTab` values from before the sub tabs existed, and the sub tab each of them selected.
const LEGACY_K8S_VULNERABILITIES_SUB_TABS = {
  0: 'cves',
  1: 'workloads',
  2: 'images',
  findings: 'findings',
  cves: 'cves',
  workloads: 'workloads',
  images: 'images',
};

/**
 * Resolve the Vulnerabilities sub tab. A link minted when the sub tabs were module tabs (e.g.
 * `?activeTab=cves` or `?activeTab=0`) opens that sub tab; the page rewrites such a URL once resolved.
 *
 * @param {string} [search] a query string, with or without the leading `?`
 * @returns {string} one of K8S_VULNERABILITIES_SUB_TABS.tabs
 */
export const resolveK8sVulnerabilitiesSubTab = (search = '') => {
  const params = new URLSearchParams(search);
  const { tabParam, tabs, defaultTab } = K8S_VULNERABILITIES_SUB_TABS;

  const tab = params.get(tabParam);
  if (tabs.includes(tab)) return tab;

  const legacyTab = LEGACY_K8S_VULNERABILITIES_SUB_TABS[params.get(K8S_VULNERABILITIES_TABS.tabParam)];
  return tabs.includes(legacyTab) ? legacyTab : defaultTab;
};

/**
 * Deep link into a Vulnerabilities sub tab. The tab params lead the query string, e.g.
 * `/kubernetes-live/vulnerabilities?activeTab=vulnerabilities&vulnerabilityTab=cves&severity=critical`.
 *
 * @param {string} [subTab] one of K8S_VULNERABILITIES_SUB_TABS.tabs; anything else falls back to its default
 * @param {Record<string, string> | string | URLSearchParams} [extraParams] deep-link params
 * @returns {string}
 */
export const k8sVulnerabilitiesSubTabPath = (
  subTab = K8S_VULNERABILITIES_SUB_TABS.defaultTab,
  extraParams = undefined,
) => {
  const { tabParam, tabs, defaultTab } = K8S_VULNERABILITIES_SUB_TABS;
  const params = new URLSearchParams({
    [K8S_VULNERABILITIES_TABS.tabParam]: 'vulnerabilities',
    [tabParam]: tabs.includes(subTab) ? subTab : defaultTab,
  });
  for (const [key, value] of new URLSearchParams(extraParams)) {
    if (key === K8S_VULNERABILITIES_TABS.tabParam || key === tabParam) continue;
    params.append(key, value);
  }
  return `${K8S_VULNERABILITIES_TABS.basePath}?${params.toString()}`;
};
